import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import EmissionCategory from "../../EmissionCategory/UI/EmissionCategory";
import "./DashboardOffset.css";
import LineChart from "../../LineChart/UI/LineChart";
import { getOffsets } from "../Application/DashboardOffset.business";
import { getSuppliersByUser } from "../../Suppliers/Application/Suppliers.business";
import { getOffsetsSelector } from "../Infrastructure/DashboardOffset.reducer";
import { getSuppliersByUserSelector } from "../../DataUser/Infrastructure/DataUser.reducer";
import { getAreasByUser } from "../../Areas/Application/Areas.business";
import { getAreasByUserSelector } from "../../DataUser/Infrastructure/DataUser.reducer";
import { getRepositoryProjectsByUserSelector } from "../../DataUser/Infrastructure/DataUser.reducer";
import { getRepositoryProjectByUser } from "../../RepositoryProjects/Application/RepositoryProjects.business";
import Emissions from "./Emissions";
import { getSelectedOffsetYearSelector } from "../../YearsUser/Infrastructure/YearsUser.reducer";
import InfoCardsSection from "./InfoCardsSection";
import BarChart from "../../BarChart/UI/BarChart";
import DonutChart from "../../DonutChart/UI/DonutChart";
import { getEmissionCategory } from "../../EmissionCategory/Aplication/EmissionCategory.business";
import { getEmissionCategorySelector } from "../../EmissionCategory/Infrastructure/EmissionCategory.reducer";

const styles = {
  container: {
    marginBottom: 20,
    marginTop: 30,
  },
  containerTons: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  ton: {
    fontFamily: "MontserratBold",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "24px",
    color: "#041370",
  },
  tons: {
    fontFamily: "MontserratBold",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "30px",
    lineHeight: "37px",
    color: "#041370",
  },
  span: {
    fontFamily: "MontserratBold",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "15px",
    lineHeight: "37px",
    color: "#041370",
  },
  scope: {
    fontFamily: "MontserratBold",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "15px",
    color: "#00A389",
  },
  down: {
    color: "#00A389",
    fontSize: 15,
    fontWeight: "bold",
  },
  up: {
    background: "#ffd9d9",
    color: "#FF1E46",
    fontSize: 15,
    fontWeight: "bold",
  },
  migrations: {
    fontSize: 15,
    color: "#00A389",
    fontWeight: 600,
  },
  loadingStyle: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },
  spinnerContainerStyle: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
};

const DashboardOffset = () => {
  const emissions = useSelector(getOffsetsSelector);
  const supplierData = useSelector(getSuppliersByUserSelector);
  const areaData = useSelector(getAreasByUserSelector);
  const repositoryProjectsData = useSelector(
    getRepositoryProjectsByUserSelector
  );
  const emissionCategoryData = useSelector(getEmissionCategorySelector || [])
  const offsetYear = useSelector(getSelectedOffsetYearSelector);

  const [areas, setAreas] = useState();
  const [suppliers, setSuppliers] = useState();
  const [repositoryProjects, setRepositoryProjects] = useState();
  const [cards, setCards] = useState({});
  const [year, setYear] = useState("");

  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      if (offsetYear) {
        await getOffsets();
      }
      await getSuppliersByUser();
      await getAreasByUser();
      await getRepositoryProjectByUser();
      await getEmissionCategory();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emissions && Object.keys(emissions).length > 0) {
      setLoading(false);
      return;
    }
  
    fetchDashboardData().finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  

  useEffect(() => {
    if (!offsetYear) return;
  
    const hasDataForYear = emissions?.year === Number(offsetYear);
    const hasEmissions = emissions && Object.keys(emissions).length > 0;
  
    if (!hasDataForYear || !hasEmissions) {
      setLoading(true);
      fetchDashboardData()
        .then(() => {
          console.log("Dashboard data fetched due to offsetYear change");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offsetYear, emissions]); 
  

  useEffect(() => {
    if (!emissions || Object.keys(emissions).length === 0) return;

    const {
      total_co2 = 0,
      emission_projected = 0,
      emission_saving = 0,
      last_month_emission = 0,
      year: emissionYear = null,
    } = emissions;

    setCards({
      total_co2,
      emission_projected,
      emission_saving,
      last_month_emission,
    });
    setYear(emissionYear);
  }, [emissions]);

  const processArray = (data) => {
    let temp = [];
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      element.arrowUp = element.up ? (
        <ArrowUpOutlined style={styles.up} />
      ) : null;
      element.arrowDown = element.down ? (
        <ArrowDownOutlined style={styles.down} />
      ) : null;
      element.iconMitigations = (
        <PlusCircleOutlined style={styles.migrations} />
      );
      temp.push(element);
    }

    return temp;
  };

  useEffect(() => {
    setAreas(processArray(areaData));
    setSuppliers(processArray(supplierData));
    setRepositoryProjects(processArray(repositoryProjectsData));
  }, [areaData, supplierData, repositoryProjectsData]);

  return (
    <div style={{ position: "relative", minHeight: "100vh" }}>
      <InfoCardsSection cards={cards} year={year} isLoading={loading} />
      <div className="grid-container">
        <div className="grid-item">
          <LineChart key={offsetYear} lineData={emissions} isLoading={loading} />
        </div>
        <div className="grid-item">
          <DonutChart key={offsetYear} donutData={emissions} isLoading={loading} />
        </div>
        <div className="grid-item">
          <BarChart key={offsetYear} barData={emissions} isLoading={loading} />
        </div>
        <div className="grid-item">
          <EmissionCategory key={offsetYear} emissionCategoryData={emissionCategoryData} isLoading={loading} />
        </div>
      </div>
      {areas?.length !== 0 && (
        <Emissions
          title={"CO₂ emission per Business Area (ton CO₂)"}
          data={areas}
          isLoading={loading}
        />
      )}
      {suppliers?.length !== 0 && (
        <Emissions
          title={"CO₂ emission per Supplier (ton CO₂)"}
          data={suppliers}
          isLoading={loading}
        />
      )}
      {repositoryProjects?.length !== 0 && (
        <Emissions
          title={"CO₂ emission per Projects (ton CO₂)"}
          data={repositoryProjects}
          isLoading={loading}
        />
      )}
      <p>
        {offsetYear ? `año seleccionado: ${offsetYear}` : "seleccionar un año"}
      </p>
    </div>
  );
};

export default DashboardOffset;
